import { Order, OrderItem, AllocatedInventoryItem } from '../types/order';
import { CompletedProduct } from '../types/todo';
import { Recipe } from '../types/recipe';

/**
 * Result of per-item inventory check for a single order item
 */
export interface ItemFulfillmentResult {
  item: OrderItem;
  hasInventory: boolean;
  availableQuantity: number;
  needsProduction: boolean;
  shortfall: number; // How much needs to be produced
}

/**
 * Result of checking order fulfillment
 */
export interface OrderFulfillmentResult {
  isFullyFulfilled: boolean; // All items have sufficient inventory
  itemResults: ItemFulfillmentResult[];
  allocatedInventory: AllocatedInventoryItem[];
}

/**
 * Checks inventory availability for each item in an order.
 *
 * Algorithm:
 * 1. For each order item, check if sufficient inventory exists
 * 2. Items WITH inventory: reserve the quantity (add to allocatedInventory)
 * 3. Items WITHOUT inventory: track the shortfall
 * 4. Mark order as ready ONLY if ALL items have sufficient inventory
 *
 * @param order - The order to check
 * @param completedProducts - Current inventory
 * @param reservedInventory - Already reserved inventory from other orders
 * @param recipes - Available recipes (fillings)
 * @returns OrderFulfillmentResult with inventory to allocate
 */
export function checkOrderItemInventory(
  order: Order,
  completedProducts: CompletedProduct[],
  reservedInventory: Map<string, number>,
  recipes: Recipe[]
): OrderFulfillmentResult {
  // Calculate total available inventory per product
  const totalInventory = new Map<string, number>();
  completedProducts.forEach(cp => {
    const current = totalInventory.get(cp.productName) || 0;
    totalInventory.set(cp.productName, current + cp.unitCount);
  });

  const itemResults: ItemFulfillmentResult[] = [];
  const allocatedInventory: AllocatedInventoryItem[] = [];
  let isFullyFulfilled = true;

  // Check each order item
  order.items.forEach(item => {
    const total = totalInventory.get(item.recipeName) || 0;
    const reserved = reservedInventory.get(item.recipeName) || 0;
    const available = total - reserved;

    const hasInventory = available >= item.quantity;
    const shortfall = hasInventory ? 0 : item.quantity - available;

    itemResults.push({
      item,
      hasInventory,
      availableQuantity: Math.max(0, available),
      needsProduction: !hasInventory,
      shortfall
    });

    if (hasInventory) {
      // Reserve inventory for this item
      allocatedInventory.push({
        productName: item.recipeName,
        quantity: item.quantity
      });

      // Update reserved quantities for subsequent items
      reservedInventory.set(item.recipeName, reserved + item.quantity);
    } else {
      // Item lacks sufficient inventory
      isFullyFulfilled = false;

      // If there's partial inventory available, reserve it
      if (available > 0) {
        allocatedInventory.push({
          productName: item.recipeName,
          quantity: available
        });
        reservedInventory.set(item.recipeName, reserved + available);
      }
    }
  });

  return {
    isFullyFulfilled,
    itemResults,
    allocatedInventory
  };
}
